import { useHandler } from "@redotech/react-util/hook";
import classNames from "classnames";
import { memo, ReactNode, useEffect, useState } from "react";
import { useVerticalScrollMask } from "../../../utils/use-vertical-scroll-mask";
import * as css from "./overflow-scroll-area.module.css";

const TRACK_INSET_PX = 8;
const MIN_THUMB_PX = 24;

interface ThumbGeometry {
  heightPx: number;
  topPx: number;
}

/**
 * A scroll container whose "there is more below" cues do not depend on the
 * OS: an always-visible bar drawn by us (overlay scrollbars on Mac and mobile
 * hide the native one until the shopper scrolls) and a fade at the clipped
 * edge. Both render only while the content actually overflows.
 */
export const OverflowScrollArea = memo(function OverflowScrollArea({
  scrollbar,
  fade,
  scrollbarColor,
  className,
  children,
}: {
  scrollbar: boolean;
  fade: boolean;
  /** Overrides the thumb color; defaults to the surrounding text color. */
  scrollbarColor?: string;
  className?: string;
  children: ReactNode;
}) {
  const { scrollRef, contentRef, handleScroll, edges, maskImage } =
    useVerticalScrollMask();
  const [thumb, setThumb] = useState<ThumbGeometry | undefined>();

  const measureThumb = useHandler(() => {
    const el = scrollRef.current;
    if (!el || !scrollbar) {
      return;
    }
    const trackPx = el.clientHeight - TRACK_INSET_PX * 2;
    const scrollable = el.scrollHeight - el.clientHeight;
    if (trackPx <= 0 || scrollable <= 1) {
      setThumb(undefined);
      return;
    }
    const heightPx = Math.max(
      MIN_THUMB_PX,
      Math.round((el.clientHeight / el.scrollHeight) * trackPx),
    );
    const topPx = Math.round(
      (el.scrollTop / scrollable) * (trackPx - heightPx),
    );
    setThumb((previous) =>
      previous?.heightPx === heightPx && previous.topPx === topPx
        ? previous
        : { heightPx, topPx },
    );
  });

  const onScroll = useHandler(() => {
    handleScroll();
    measureThumb();
  });

  // Content and container can both change size after mount (images loading,
  // the block being resized in the builder).
  useEffect(() => {
    measureThumb();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(measureThumb);
    observer.observe(el);
    if (contentRef.current) {
      observer.observe(contentRef.current);
    }
    return () => observer.disconnect();
  }, [measureThumb, scrollRef, contentRef, scrollbar, edges.scrollable]);

  const fadeMask = fade ? maskImage : undefined;
  const showBar = scrollbar && edges.scrollable && thumb !== undefined;

  return (
    <div className={classNames(css.root, className)}>
      <div
        className={css.scroll}
        data-scroll-container
        onScroll={onScroll}
        ref={scrollRef}
        style={{ maskImage: fadeMask, WebkitMaskImage: fadeMask }}
      >
        <div ref={contentRef}>{children}</div>
      </div>
      {showBar && (
        <div aria-hidden className={css.track} data-overflow-scrollbar>
          <div
            className={css.thumb}
            style={{
              height: `${thumb.heightPx}px`,
              top: `${thumb.topPx}px`,
              ...(scrollbarColor ? { background: scrollbarColor } : {}),
            }}
          />
        </div>
      )}
    </div>
  );
});
